"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { MagnifyingGlass, WarningCircle } from "@phosphor-icons/react";
import styles from "@/app/plaza/plaza.module.css";
import { PlazaInfiniteGrid } from "./PlazaInfiniteGrid";
import { PlazaFilterSelect } from "./PlazaFilterSelect";
import { PlazaPopularSearch } from "./PlazaPopularSearch";
import { PlazaLiveSearch } from "./PlazaLiveSearch";
import { plazaSearchParams } from "../server/filters";
import { fetchPlazaSearch } from "../search";
import { plazaCategories, plazaPopularQueries, type PlazaSort, type PlazaFilters, type PlazaResult } from "../types";

import { plazaBrowseHref } from "../browse-navigation";
import type { PlazaCastingContext } from "./PlazaCastingActions";
import { filterCatalog } from "@/features/projects/catalog";
import type { PlazaAvatar } from "../types";

const sortLabels: Record<PlazaSort, string> = {
  recommend: "推荐",
  latest: "最新",
  fans: "粉丝量榜",
  popular: "受欢迎度榜",
};

export function PlazaBrowse({ initialFilters, initialResult, casting, favoriteItems, basePath = "/plaza" }: {
  casting?: PlazaCastingContext;
  favoriteItems?: PlazaAvatar[];
  basePath?: string;
  initialFilters: PlazaFilters;
  initialResult: PlazaResult;
  publicProxy: boolean;
}) {
  function browseHref(value: PlazaFilters, overrides: Partial<PlazaFilters> = {}) {
    return plazaBrowseHref(value, overrides, { basePath, project: casting?.project, favoritesOnly: Boolean(favoriteItems) });
  }
  const resetHref = browseHref({ ...initialFilters, query: "", popular: [], tags: [], category: undefined, sort: "recommend", page: 1 });
  const [filters, setFilters] = useState(initialFilters);
  const [result, setResult] = useState(initialResult);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestRef = useRef<AbortController | null>(null);
  const [previousResult, setPreviousResult] = useState(initialResult);
  if (previousResult !== initialResult) {
    setPreviousResult(initialResult);
    setFilters(initialFilters);
    setResult(initialResult);
    setBusy(false);
    setFailed(false);
  }
  useEffect(() => {
    requestRef.current = null;
    return () => requestRef.current?.abort();
  }, [initialResult]);

  function cancelSearch() {
    requestRef.current?.abort();
    requestRef.current = null;
    setBusy(false);
    setFailed(false);
  }

  async function search(query: string) {
    cancelSearch();
    const controller = new AbortController();
    requestRef.current = controller;
    const nextFilters = { ...filters, query, page: 1 };
    setBusy(true);
    try {
      const rows = favoriteItems ? filterCatalog(favoriteItems, nextFilters) : undefined;
      const nextResult = rows ? { ...initialResult, items: rows, total: rows.length, page: 1, pageCount: 1 } : await fetchPlazaSearch(nextFilters, controller.signal);
      if (controller.signal.aborted) return;
      setResult(nextResult);
      setFilters(nextFilters);
      // Keep deep links current without requesting the entire server-rendered page.
      window.history.replaceState(null, "", browseHref(nextFilters));
    } catch {
      if (!controller.signal.aborted) setFailed(true);
    } finally {
      if (!controller.signal.aborted) {
        requestRef.current = null;
        setBusy(false);
      }
    }
  }

  return (
    <div className={`${styles.page} ${casting ? styles.embedded : "min-h-screen pt-16"}`}>
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          {casting ? <h1 className="sr-only">选角</h1> : <>
          <p className={styles.eyebrow}>MIRAFANS · AI CASTING</p>
          <h1 className={styles.title}>形象<span>广场</span></h1>
          <p className={styles.description}>发现可授权的真人数字形象，找到适合下一部作品的个体授权方。</p>
          </>}
          <div className={styles.search}>
            <PlazaLiveSearch onSearch={search} onQueryChange={cancelSearch} busy={busy} failed={failed} initialQuery={filters.query} tags={filters.tags} category={filters.category} sort={filters.sort} />
          </div>
          <div className={styles.filters}>
            <PlazaPopularSearch
              className={styles.hot}
              basePath={basePath}
              selected={[...new Set([...(filters.popular ?? []), ...plazaPopularQueries.filter(query => query === filters.query)])]}
              options={[...plazaPopularQueries]}
            />
            <PlazaFilterSelect
              label="排序"
              value={filters.sort}
              options={(Object.keys(sortLabels) as PlazaSort[]).map(sort => ({
                value: sort,
                label: sortLabels[sort],
                href: browseHref(filters, { sort, page: 1 }),
              }))}
            />
            <PlazaFilterSelect
              label="形象分类"
              value={filters.category ?? ""}
              options={[
                { value: "", label: "全部", href: browseHref(filters, { category: undefined, page: 1 }) },
                ...plazaCategories.map(category => ({
                  value: category,
                  label: category,
                  href: browseHref(filters, { category, page: 1 }),
                })),
              ]}
            />
          </div>
        </div>
      </section>

      <section className={styles.catalog}>


        {result.compatibilityMode && (
          <p className="mt-5 rounded-[var(--radius-input)] border border-[var(--color-line)] bg-[var(--color-tint)] px-4 py-3 text-xs leading-5 text-[var(--color-muted)]">
            广场正在使用兼容读取模式；形象均为已上架数据，部分榜单与分类会在公共投影升级后补齐。
          </p>
        )}

        {result.catalog.source === "demo" && (
          <p className="mt-5 rounded-[var(--radius-input)] border border-[var(--color-line)] bg-[var(--color-tint)] px-4 py-3 text-xs leading-5 text-[var(--color-muted)]">
            当前为明确标记的本地演示目录；正式环境只展示从小程序公共投影同步的已上架形象。
          </p>
        )}

        {result.catalog.initialized && result.catalog.status === "failed" && (
          <p className="mt-5 rounded-[var(--radius-input)] border border-[var(--color-line)] bg-[var(--color-tint)] px-4 py-3 text-xs leading-5 text-[var(--color-muted)]">
            最新同步暂不可用，当前继续展示上一次成功的完整快照，不会清空或显示半批数据。
          </p>
        )}

        {!result.catalog.initialized ? (
          <div className="mt-8 grid place-items-center rounded-[var(--radius-card)] border border-dashed border-[var(--color-line-strong)] px-6 py-20 text-center">
            <WarningCircle size={30} className="text-[var(--color-warm)]" aria-hidden="true" />
            <h2 className="mt-4 text-base font-semibold">公共形象目录尚未初始化</h2>
            <p className="mt-2 max-w-lg text-sm leading-6 text-[var(--color-muted)]">网站只保存审核后可公开字段；完成一次受控同步后，页面将从 Web 本地快照稳定读取，不再依赖每次请求临时连接小程序后台。</p>
          </div>
        ) : result.items.length > 0 ? (
          <>
            {(filters.query || filters.popular?.length || filters.category || filters.tags?.length || filters.sort !== "recommend") && (
              <div className="mt-8 flex items-baseline justify-between gap-4">
                <h2 className="text-xl font-semibold">{filters.sort === "latest" && !filters.query && !filters.popular?.length && !filters.category && !filters.tags?.length ? "最新形象" : "筛选结果"}</h2>
                <Link href={resetHref} className="text-xs font-medium text-[var(--color-accent)]">重置筛选</Link>
              </div>
            )}
            <PlazaInfiniteGrid key={plazaSearchParams(filters)} filters={filters} initialResult={result} casting={casting} />
          </>
        ) : (
          <div className="mt-8 grid place-items-center rounded-[var(--radius-card)] border border-dashed border-[var(--color-line-strong)] px-6 py-20 text-center">
            <MagnifyingGlass size={30} className="text-[var(--color-faint)]" aria-hidden="true" />
            <h2 className="mt-4 text-base font-semibold">没有找到匹配的形象</h2>
            <p className="mt-2 text-sm text-[var(--color-muted)]">试试更短的名称、风格或题材关键词，或减少筛选条件。</p>
            <Link href={resetHref} className="mt-3 text-sm font-medium text-[var(--color-accent)]">查看全部形象</Link>
          </div>
        )}
      </section>
    </div>
  );
}
